import React from "react";
import { ArrowRight, ShieldCheck, LifeBuoy, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { StatCounter } from "@/components/shared/StatCounter";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { useConsultation } from "@/components/forms/ConsultationDialog";
import { TEAM, WHY_US, HERO_STATS, IMAGES } from "@/data/content";

export default function About() {
  const { open } = useConsultation();
  return (
    <>
      <PageHero
        kicker="About Us"
        title="The advisor you keep."
        subtitle="We built this firm on relationships, not transactions — led by former CxOs and carrier-side engineers who have sat in your chair, and theirs."
        image={IMAGES.boardroom}
      >
        <Button variant="hero" size="xl" onClick={open}>
          Talk to a Former CxO Partner <ArrowRight className="h-5 w-5" />
        </Button>
      </PageHero>

      {/* Metrics */}
      <section className="section-x py-16">
        <div className="container-tmt grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          {HERO_STATS.map((s) => (
            <div key={s.label} className="bg-card p-7"><StatCounter value={s.value} label={s.label} note={s.note} /></div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="section-x py-12 sm:py-20">
        <div className="container-tmt">
          <SectionHeading
            kicker="Why Us"
            title="Veteran expertise. Relationships that outlast contracts."
            subtitle="We provide deep strategic support — TCO modeling, business-case justification, and a post-sale model that keeps working for you."
          />
          <RevealStagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_US.map((w, i) => {
              const icons = [ShieldCheck, Users, ShieldCheck, LifeBuoy];
              const Icon = icons[i % icons.length];
              return (
                <RevealItem key={w.title}>
                  <div className="card-hairline relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 transition-colors duration-300 hover:border-primary/40">
                    <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-6 w-6" /></span>
                    <h3 className="font-display text-lg font-semibold">{w.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{w.desc}</p>
                  </div>
                </RevealItem>
              );
            })}
          </RevealStagger>
        </div>
      </section>

      {/* Team */}
      <section className="section-x py-12 sm:py-20">
        <div className="container-tmt">
          <SectionHeading kicker="Leadership" title="Practitioners, not salespeople" subtitle="The people advising you have run the audits, passed the compliance reviews, and negotiated the contracts from both sides of the table." />
          <RevealStagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM.map((m) => (
              <RevealItem key={m.name}>
                <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-7">
                  <Avatar className="h-16 w-16 border border-primary/30">
                    <AvatarFallback className="bg-gradient-primary font-display text-lg font-semibold text-primary-foreground">{m.initials}</AvatarFallback>
                  </Avatar>
                  <h3 className="mt-5 font-display text-lg font-semibold">{m.name}</h3>
                  <p className="font-mono text-xs text-primary">{m.title}</p>
                  <p className="mt-3 text-sm text-muted-foreground">{m.bio}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Post-sale model */}
      <section className="section-x pb-8">
        <div className="container-tmt">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-surface p-8 sm:p-12">
              <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/15 blur-3xl" />
              <div className="relative max-w-3xl">
                <span className="kicker">The Post-Sale Life-of-Contract Model</span>
                <h2 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">We stay in the room after the ink dries.</h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  If a supplier underperforms, we don’t send you back to the vendor’s support queue. We escalate on your behalf, hold them to the SLA, and keep benchmarking your spend against a market that never stops moving.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand title="Let's build a relationship that outlasts the contract." subtitle="Schedule a consultation with a partner who has sat in your seat." />
    </>
  );
}
